/**
 * Local-database health, as state the UI actually reacts to.
 *
 * The storage failures this tracks are the ones that matter most on a browser
 * till, because they do not look like failures:
 *
 *   • Quota exceeded — WatermelonDB falls back to an IN-MEMORY database. The
 *     till keeps billing, receipts keep printing, and nothing is persisted.
 *     Every sale since the failure disappears on reload.
 *   • Another tab overwrote this tab's IndexedDB, or bumped its version. Writes
 *     from here are landing in a database that is no longer authoritative.
 *
 * This lived in a module-level `let` in db/index.ts, which could not work:
 * the LokiJS callbacks that set it fire asynchronously, long after boot, and
 * assigning a module variable does not re-render React. The failure screen was
 * only reachable if something else happened to re-render, and the invoice
 * writer never consulted it at all — so the worst failure mode in the product
 * had a handler that, in practice, did nothing.
 *
 * Kept deliberately tiny and dependency-free so `db/index.ts` can import it
 * without a cycle, and so non-React callers (the billing writers) can read it
 * synchronously via `getDbError()`.
 */
import { create } from 'zustand';

interface DbHealthState {
  /** Non-null means the local database cannot be trusted for writes. */
  error: string | null;
  setError: (message: string | null) => void;
}

export const useDbHealthStore = create<DbHealthState>()((set) => ({
  error: null,
  setError: (message) => set({ error: message }),
}));

/**
 * Records a fatal storage condition.
 *
 * Idempotent on the message so a callback that fires repeatedly (a quota error
 * on every write attempt) does not churn subscribers.
 */
export function setDbError(message: string | null): void {
  const { error, setError } = useDbHealthStore.getState();
  if (error === message) return;
  setError(message);
}

/**
 * Synchronous read, for the write boundaries.
 *
 * Billing must be able to ask "is the database safe to write to?" outside a
 * React render, and must get the current answer rather than one captured at
 * module load.
 */
export function getDbError(): string | null {
  return useDbHealthStore.getState().error;
}
